/**
 * SectionReviewPage — supervised generation section review UI.
 *
 * Content-based: routes via /content/supervised-review/:contentId
 * Uses the P1.7 content-based API endpoints:
 *   GET  /supervised/content/:id/section-review
 *   POST /supervised/content/:id/section-action
 *   GET  /supervised/content/:id/progress
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import {
  Check,
  Edit3,
  RefreshCw,
  Loader2,
  CheckCircle2,
  AlertCircle,
  BarChart3,
  MessageSquare,
  ArrowLeft,
  Zap,
  ChevronDown,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  useSectionReview,
  useSectionAction,
  useSupervisedProgress,
} from '@/hooks/useSupervisedGeneration';
import { OutlinePendingView } from './OutlinePendingView';
import type { TipTapBlock, SectionScore, BenchmarkResult } from '@/services/supervisedGenerationService';


// ---------------------------------------------------------------------------
// Score badge
// ---------------------------------------------------------------------------

function gradeColor(grade: string): string {
  if (grade === 'A' || grade === 'A+') return 'text-emerald-600 bg-emerald-50';
  if (grade === 'B' || grade === 'B+') return 'text-teal-dark bg-teal-light';
  if (grade === 'C' || grade === 'C+') return 'text-amber-600 bg-amber-50';
  return 'text-destructive bg-destructive/5';
}

function ScoreBadge({ score }: { score: SectionScore }) {
  const [expanded, setExpanded] = useState(false);

  const categories = [
    { label: 'Formatting', value: score.formatting, color: 'bg-blue-500' },
    { label: 'Factual Accuracy', value: score.factual_accuracy, color: 'bg-emerald-500' },
    { label: 'Originality', value: score.originality, color: 'bg-purple-500' },
    { label: 'Search Intent', value: score.search_intent, color: 'bg-coral' },
    { label: 'Brand Voice', value: score.brand_voice, color: 'bg-teal' },
  ].filter((c) => c.value !== undefined);

  return (
    <div className="relative">
      <button
        onClick={() => setExpanded(!expanded)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-border transition-all ${gradeColor(score.grade)}`}
      >
        <BarChart3 className="h-3 w-3" />
        {score.total}/100 ({score.grade})
        <ChevronDown className={`h-3 w-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="absolute right-0 top-full mt-2 z-50 bg-white border rounded-xl shadow-lg p-4 w-72">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium">Quality Breakdown</span>
            <span className={`text-lg font-bold ${gradeColor(score.grade)} px-2 py-0.5 rounded`}>
              {score.grade}
            </span>
          </div>
          <div className="space-y-2.5">
            {categories.map((cat) => (
              <div key={cat.label}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-muted-foreground">{cat.label}</span>
                  <span className="font-medium">{cat.value}%</span>
                </div>
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${cat.color}`}
                    style={{ width: `${cat.value}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t text-center">
            <span className="text-xs text-muted-foreground">
              Overall: <span className="font-medium">{score.total}/100</span>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Block renderer (read-only TipTap JSON → HTML)
// ---------------------------------------------------------------------------

function renderBlockText(block: TipTapBlock): string {
  if (!block.content) return '';
  return block.content
    .filter((c) => c.type === 'text')
    .map((c) => c.text || '')
    .join('');
}

function countBlockWords(blocks: TipTapBlock[]): number {
  return blocks.reduce((total, block) => {
    const text = renderBlockText(block);
    return total + (text ? text.split(/\s+/).filter(Boolean).length : 0);
  }, 0);
}

function BlocksDisplay({ blocks, skipFirstH2 = true }: { blocks: TipTapBlock[]; skipFirstH2?: boolean }) {
  // Filter out the first H2 heading to avoid duplicating the section card title
  let filteredBlocks = blocks;
  if (skipFirstH2) {
    let skipped = false;
    filteredBlocks = blocks.filter((b) => {
      if (!skipped && b.type === 'heading' && b.attrs?.level === 2) {
        skipped = true;
        return false;
      }
      return true;
    });
  }

  // Render TipTap blocks as HTML directly — avoids editor initialization issues
  const renderNode = (node: any, idx: number): React.ReactNode => {
    if (!node) return null;

    // Text node
    if (node.type === 'text') {
      let text: React.ReactNode = node.text || '';
      if (node.marks) {
        for (const mark of node.marks) {
          if (mark.type === 'bold') text = <strong key={`b${idx}`}>{text}</strong>;
          else if (mark.type === 'italic') text = <em key={`i${idx}`}>{text}</em>;
          else if (mark.type === 'link') text = <a key={`l${idx}`} href={mark.attrs?.href} className="text-coral underline" target="_blank" rel="noopener noreferrer">{text}</a>;
        }
      }
      return text;
    }

    const children = (node.content || []).map((child: any, i: number) => renderNode(child, i));

    switch (node.type) {
      case 'heading': {
        const level = node.attrs?.level || 2;
        if (level === 1) return <h1 key={idx} className="text-xl font-bold mt-4 mb-2">{children}</h1>;
        if (level === 2) return <h2 key={idx} className="text-lg font-semibold mt-4 mb-2">{children}</h2>;
        return <h3 key={idx} className="text-base font-medium mt-3 mb-1">{children}</h3>;
      }
      case 'paragraph':
        return <p key={idx} className="mb-3 leading-relaxed">{children}</p>;
      case 'bulletList':
        return <ul key={idx} className="list-disc pl-5 mb-3 space-y-1">{children}</ul>;
      case 'orderedList':
        return <ol key={idx} className="list-decimal pl-5 mb-3 space-y-1">{children}</ol>;
      case 'listItem':
        return <li key={idx}>{children}</li>;
      case 'blockquote':
        return <blockquote key={idx} className="border-l-4 border-coral/30 pl-4 italic text-muted-foreground mb-3">{children}</blockquote>;
      case 'callout':
        return (
          <div key={idx} className="bg-teal-light border border-teal/20 rounded-lg p-4 mb-3">
            <div className="text-sm">{children}</div>
          </div>
        );
      case 'table':
        return (
          <table key={idx} className="w-full border-collapse mb-3 text-sm">
            <tbody>{children}</tbody>
          </table>
        );
      case 'tableRow':
        return <tr key={idx} className="border-b border-border">{children}</tr>;
      case 'tableHeader':
        return <th key={idx} className="text-left p-2 bg-muted font-medium">{children}</th>;
      case 'tableCell':
        return <td key={idx} className="p-2">{children}</td>;
      case 'faqSchema':
        return <div key={idx} className="mb-4 space-y-3 border rounded-lg p-4 bg-muted/50">{children}</div>;
      case 'faqItem':
        return <div key={idx} className="border-b last:border-0 pb-3 last:pb-0">{children}</div>;
      case 'faqQuestion':
        // FAQ questions publish as <h3 itemprop="name">, so preview them as a
        // real h3 (matching the level-3 heading style above) rather than a
        // styled paragraph — otherwise the preview misrepresents the heading
        // level and reviewers can't verify it.
        return <h3 key={idx} className="text-base font-medium mt-3 mb-1">{children}</h3>;
      case 'faqAnswer':
        return <div key={idx} className="text-sm text-muted-foreground pl-3">{children}</div>;
      case 'image': {
        const src = node.attrs?.src || '';
        const alt = node.attrs?.alt || '';
        const isYoutube = src.includes('ytimg.com');
        return (
          <div key={idx} className="mb-3">
            <img src={src} alt={alt} className="rounded-lg max-h-48 w-auto border" />
            {alt && (
              <p className="text-xs text-muted-foreground mt-1 italic">{alt}</p>
            )}
            {isYoutube && (
              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 mt-1 rounded-full bg-coral-light text-coral-dark">▶ YouTube Video</span>
            )}
            {!isYoutube && (
              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 mt-1 rounded-full bg-teal-light text-teal-dark">📷 Stock Photo</span>
            )}
          </div>
        );
      }
      case 'youtube': {
        const embedSrc = node.attrs?.src || '';
        return (
          <div key={idx} className="mb-3 rounded-lg overflow-hidden border" style={{ maxWidth: 480 }}>
            <iframe src={embedSrc} width="100%" height="270" frameBorder="0" allowFullScreen title="YouTube video" />
          </div>
        );
      }
      case 'rawHtml': {
        // CTA block — render as styled card
        const htmlText = (node.content || []).map((c: any) => c.text || '').join('');
        const plainText = htmlText.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
        return (
          <div key={idx} className="mb-3 p-4 rounded-lg bg-coral-light border border-coral/20 text-center">
            <p className="font-semibold text-coral-dark">{plainText || 'Call to Action'}</p>
          </div>
        );
      }
      case 'codeBlock':
        return <pre key={idx} className="bg-muted p-3 rounded text-sm mb-3 overflow-x-auto"><code>{children}</code></pre>;
      case 'hardBreak':
        return <br key={idx} />;
      default:
        // Unknown node type — render children or text
        return children.length > 0 ? <div key={idx}>{children}</div> : null;
    }
  };

  return (
    <div className="text-sm text-foreground">
      {filteredBlocks.map((block, idx) => renderNode(block, idx))}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Section editor (TipTap inline editor for edit mode)
// ---------------------------------------------------------------------------

function cleanBlocksForTipTap(blocks: TipTapBlock[]): any {
  const clean = (node: any): any => {
    if (!node || typeof node !== 'object') return node;

    // Convert callout → blockquote for TipTap (callout not in StarterKit)
    const type = node.type === 'callout' ? 'blockquote' : node.type;
    const result: any = { type };

    if (node.attrs) {
      const safe: any = {};
      if (node.attrs.level != null) safe.level = node.attrs.level;
      if (node.attrs.start != null) safe.start = node.attrs.start;
      if (node.attrs.href) safe.href = node.attrs.href;
      if (node.attrs.target) safe.target = node.attrs.target;
      // Table attrs
      if (node.attrs.colspan != null) safe.colspan = node.attrs.colspan;
      if (node.attrs.rowspan != null) safe.rowspan = node.attrs.rowspan;
      if (node.attrs.colwidth) safe.colwidth = node.attrs.colwidth;
      if (Object.keys(safe).length > 0) result.attrs = safe;
    }
    if (node.text !== undefined) result.text = node.text;
    if (node.marks) result.marks = node.marks;
    if (node.content) result.content = node.content.map(clean);
    return result;
  };
  return blocks.map(clean);
}

function SectionEditor({
  blocks,
  onSave,
  onCancel,
  isPending,
}: {
  blocks: TipTapBlock[];
  onSave: (editedBlocks: TipTapBlock[], feedback?: string) => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  const [feedback, setFeedback] = useState('');

  // Build clean TipTap doc — exclude H2 heading + unsupported node types (image, youtube, faqSchema, rawHtml)
  const UNSUPPORTED_TYPES = new Set(['image', 'youtube', 'faqSchema', 'rawHtml', 'callout']);
  const cleanDoc = useMemo(() => {
    const editableBlocks = blocks.filter(b => {
      if (b.type === 'heading' && b.attrs?.level === 2) return false; // H2 shown as card title
      if (UNSUPPORTED_TYPES.has(b.type)) return false; // Can't edit these in TipTap
      return true;
    });
    return {
      type: 'doc' as const,
      content: cleanBlocksForTipTap(editableBlocks),
    };
  }, [blocks]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [3, 4] },
      }),
      Link.configure({
        openOnClick: false,
        autolink: false,
        linkOnPaste: true,
        HTMLAttributes: {
          class: 'text-coral underline cursor-pointer',
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Table.configure({ resizable: false }),
      TableRow,
      TableCell,
      TableHeader,
    ],
    content: cleanDoc,
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[200px] p-4 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-base [&_h3]:font-medium [&_h3]:mt-3 [&_h3]:mb-1 [&_p]:mb-3 [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_blockquote]:border-l-4 [&_blockquote]:border-teal/30 [&_blockquote]:bg-teal-light/50 [&_blockquote]:pl-4 [&_blockquote]:py-2 [&_blockquote]:rounded-r-lg [&_blockquote]:italic [&_table]:w-full [&_table]:border-collapse [&_table]:mb-3 [&_th]:bg-muted [&_th]:text-left [&_th]:p-2 [&_th]:font-medium [&_th]:border [&_th]:border-border [&_td]:p-2 [&_td]:border [&_td]:border-border',
      },
    },
  });

  const handleSave = useCallback(() => {
    if (!editor) return;
    const doc = editor.getJSON();
    // Re-add the main H2 heading at the start (excluded from editor)
    const h2 = blocks.find(b => b.type === 'heading' && b.attrs?.level === 2);
    const editedBlocks: TipTapBlock[] = [];
    if (h2) editedBlocks.push(h2);
    if (doc.content) {
      editedBlocks.push(...(doc.content as TipTapBlock[]));
    }
    onSave(editedBlocks, feedback || undefined);
  }, [editor, blocks, feedback, onSave]);

  return (
    <div className="space-y-3">
      {/* H2 heading (read-only — section title, not editable in TipTap) */}
      {blocks.find(b => b.type === 'heading' && b.attrs?.level === 2) && (
        <h2 className="text-lg font-semibold text-muted-foreground">
          {renderBlockText(blocks.find(b => b.type === 'heading' && b.attrs?.level === 2)!)}
        </h2>
      )}

      <div className="border rounded-lg overflow-hidden">
        {/* Formatting toolbar */}
        {editor && (
          <div className="flex items-center gap-0.5 px-2 py-1.5 border-b bg-muted flex-wrap">
            <button
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              className={`px-2 py-1 text-xs rounded ${editor.isActive('heading', { level: 3 }) ? 'bg-coral/10 text-coral font-semibold' : 'hover:bg-muted'}`}
            >
              H3
            </button>
            <div className="w-px h-4 bg-border mx-1" />
            <button
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={`px-2 py-1 text-xs rounded font-bold ${editor.isActive('bold') ? 'bg-coral/10 text-coral' : 'hover:bg-muted'}`}
            >
              B
            </button>
            <button
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={`px-2 py-1 text-xs rounded italic ${editor.isActive('italic') ? 'bg-coral/10 text-coral' : 'hover:bg-muted'}`}
            >
              I
            </button>
            <div className="w-px h-4 bg-border mx-1" />
            <button
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={`px-2 py-1 text-xs rounded ${editor.isActive('bulletList') ? 'bg-coral/10 text-coral' : 'hover:bg-muted'}`}
            >
              • List
            </button>
            <button
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className={`px-2 py-1 text-xs rounded ${editor.isActive('orderedList') ? 'bg-coral/10 text-coral' : 'hover:bg-muted'}`}
            >
              1. List
            </button>
            <div className="w-px h-4 bg-border mx-1" />
            <button
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              className={`px-2 py-1 text-xs rounded ${editor.isActive('blockquote') ? 'bg-coral/10 text-coral' : 'hover:bg-muted'}`}
            >
              Quote
            </button>
            <button
              onClick={() => {
                const raw = window.prompt('Enter URL:');
                if (raw) {
                  const url = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
                  editor.chain().focus().setLink({ href: url, target: '_blank' }).run();
                } else {
                  editor.chain().focus().unsetLink().run();
                }
              }}
              className={`px-2 py-1 text-xs rounded ${editor.isActive('link') ? 'bg-coral/10 text-coral' : 'hover:bg-muted'}`}
            >
              Link
            </button>
            <div className="w-px h-4 bg-border mx-1" />
            <button
              onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
              className={`px-2 py-1 text-xs rounded ${editor.isActive('table') ? 'bg-coral/10 text-coral' : 'hover:bg-muted'}`}
            >
              Table
            </button>
            <button
              onClick={() => {
                // Insert a callout as blockquote with pro-tip content
                editor.chain().focus()
                  .toggleBlockquote()
                  .insertContent({ type: 'paragraph', content: [{ type: 'text', marks: [{ type: 'bold' }], text: 'Pro Tip: ' }] })
                  .run();
              }}
              className={`px-2 py-1 text-xs rounded ${editor.isActive('blockquote') ? 'bg-teal/10 text-teal' : 'hover:bg-muted'}`}
            >
              Pro Tip
            </button>
          </div>
        )}
        <EditorContent editor={editor} />
      </div>

      <div className="flex items-start gap-2">
        <MessageSquare className="h-4 w-4 mt-2.5 text-muted-foreground shrink-0" />
        <textarea
          placeholder="Optional feedback for the next generation..."
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          className="flex-1 px-3 py-2 text-sm border rounded-lg resize-none"
          rows={2}
        />
      </div>

      <div className="flex gap-2">
        <button
          onClick={handleSave}
          disabled={isPending}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-coral text-white text-sm rounded-lg hover:bg-coral-hover disabled:opacity-50"
        >
          {isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          Save Edit
        </button>
        <button
          onClick={onCancel}
          className="px-3 py-1.5 border text-sm rounded-lg hover:bg-muted"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Progress sidebar
// ---------------------------------------------------------------------------

function ProgressSidebar({
  totalSections,
  approvedCount,
  currentIndex,
  autoApproveActive,
  autoApprovedCount,
  viewingIndex,
  onSectionClick,
  sectionHeadings = [],
}: {
  totalSections: number;
  approvedCount: number;
  currentIndex: number;
  autoApproveActive: boolean;
  autoApprovedCount: number;
  viewingIndex: number | null;
  onSectionClick: (index: number | null) => void;
  sectionHeadings?: string[];
}) {
  const sections = Array.from({ length: totalSections }, (_, i) => i);
  const manualApprovedCount = approvedCount - autoApprovedCount;

  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        Progress
      </div>
      {sections.map((idx) => {
        const isApproved = idx < approvedCount;
        const isCurrent = idx === currentIndex;
        const isAutoApproved = isApproved && idx >= manualApprovedCount && autoApprovedCount > 0;
        const isViewing = viewingIndex === idx;
        const isFuture = !isApproved && !isCurrent;

        return (
          <button
            key={idx}
            onClick={() => {
              if (isCurrent) onSectionClick(null);
              else onSectionClick(idx);
            }}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
              isViewing
                ? 'bg-teal-light text-teal-dark font-medium ring-1 ring-teal/30'
                : isCurrent && viewingIndex === null
                  ? 'bg-coral-light text-coral-dark font-medium'
                  : isApproved
                    ? 'text-emerald-600 hover:bg-emerald-50 cursor-pointer'
                    : isFuture
                      ? 'text-muted-foreground/50 hover:bg-muted cursor-pointer'
                      : 'text-muted-foreground'
            }`}
          >
            {isApproved ? (
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
            ) : isCurrent ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-coral shrink-0" />
            ) : (
              <div className="h-3.5 w-3.5 rounded-full border border-border shrink-0" />
            )}
            <span className="truncate">{sectionHeadings[idx] || `Section ${idx + 1}`}</span>
            {isAutoApproved && (
              <span className="text-[10px] text-teal-dark bg-teal-light px-1.5 py-0.5 rounded-full shrink-0">
                auto
              </span>
            )}
          </button>
        );
      })}

      <div className="pt-3 border-t mt-3">
        <div className="text-xs text-muted-foreground">
          {approvedCount} / {totalSections} approved
          {autoApprovedCount > 0 && ` (${autoApprovedCount} auto)`}
        </div>
        {autoApproveActive && (
          <div className="text-xs text-teal-dark mt-1 flex items-center gap-1">
            <Zap className="h-3 w-3" />
            Auto-approve active
          </div>
        )}
      </div>

      {/* Auto-generated blocks footer */}
      <div className="pt-3 border-t mt-3">
        <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-2">
          Auto-generated
        </div>
        <div className="space-y-1">
          {['Key Takeaways', 'FAQ', 'CTA'].map((label) => (
            <div key={label} className="flex items-center gap-2 px-3 py-1.5 text-xs text-muted-foreground/60">
              <CheckCircle2 className="h-3 w-3 text-muted-foreground/50 shrink-0" />
              <span>{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function SectionReviewPage() {
  const { contentId } = useParams<{ contentId: string }>();
  const navigate = useNavigate();

  // Back navigation: prefer browser-back (restores planner URL state + scroll),
  // fall back to the planner table view when there's no history.
  const goBack = useCallback(() => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/content/planner?view=table');
  }, [navigate]);

  const [isEditing, setIsEditing] = useState(false);
  const [editedHeading, setEditedHeading] = useState('');
  const [regenFeedback, setRegenFeedback] = useState('');
  const [showRegenFeedback, setShowRegenFeedback] = useState(false);
  const [viewSection, setViewSection] = useState<number | null>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [generatingNextIdx, setGeneratingNextIdx] = useState<number | null>(null);

  const { data: review, isLoading, isError, error } = useSectionReview(contentId ?? null, viewSection);
  const { data: progress } = useSupervisedProgress(contentId ?? null);
  const actionMutation = useSectionAction();

  // Clear generating state when the next section is ready
  useEffect(() => {
    if (generatingNextIdx !== null && review) {
      // Section is ready when review.section_index matches what we're waiting for
      // OR when the status indicates review is available (section_N_review)
      const statusMatch = review.status && /^section_\d+_review$/.test(review.status);
      if (review.section_index === generatingNextIdx || statusMatch) {
        setGeneratingNextIdx(null);
      }
    }
  }, [review, generatingNextIdx]);

  // Show final review when generation completes (don't auto-redirect)
  const [showFinalReview, setShowFinalReview] = useState(false);
  useEffect(() => {
    if (contentId && (progress?.status === 'completed' || progress?.status === 'published')) {
      setShowFinalReview(true);
    }
  }, [contentId, progress?.status]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  // Track when we're waiting for outline approval to process
  const [outlineApproving, setOutlineApproving] = useState(false);

  const handleApprove = useCallback((userSelections?: any) => {
    if (!contentId) return;
    const action = review?.status === 'outline_pending' ? 'approve_outline' : 'approve';
    const nextIdx = (review?.section_index ?? 0) + 1;
    const payload: any = { action };
    if (action === 'approve_outline' && userSelections) {
      payload.user_selections = userSelections;
    }
    actionMutation.mutate(
      { contentId, payload },
      {
        onSuccess: () => {
          toast.success(action === 'approve_outline' ? 'Outline approved — generating first section...' : 'Section approved — generating next...');
          setIsEditing(false);
          setViewSection(null);
          setIsRegenerating(false);
          if (action === 'approve_outline') {
            // Show generating state immediately — don't wait for poll
            setOutlineApproving(true);
          } else {
            setGeneratingNextIdx(nextIdx);
          }
        },
        onError: (err) => {
          toast.error(`Failed to approve: ${(err as any)?.response?.data?.detail || err.message}`);
        },
      },
    );
  }, [contentId, actionMutation, review?.status, review?.section_index]);

  // Clear outlineApproving when review status changes away from outline_pending
  useEffect(() => {
    if (outlineApproving && review?.status && review.status !== 'outline_pending') {
      setOutlineApproving(false);
    }
  }, [outlineApproving, review?.status]);

  const handleEdit = useCallback(
    (editedBlocks: TipTapBlock[], feedback?: string) => {
      if (!contentId) return;
      // Prepend the H2 heading block (may have been edited)
      const headingBlock: TipTapBlock = {
        type: 'heading',
        attrs: { level: 2 },
        content: [{ type: 'text', text: editedHeading || review?.heading || '' }],
      };
      const fullBlocks = [headingBlock, ...editedBlocks];
      actionMutation.mutate(
        { contentId, payload: { action: 'edit', edited_blocks: fullBlocks, feedback } },
        {
          onSuccess: () => {
            toast.success('Edit saved — generating next section');
            setIsEditing(false);
            setViewSection(null);
          },
          onError: (err) => {
            toast.error(`Failed to save edit: ${(err as any)?.response?.data?.detail || err.message}`);
          },
        },
      );
    },
    [contentId, actionMutation],
  );

  // Track regen count to detect when regeneration completes
  const regenCountAtStart = useRef<number | null>(null);
  useEffect(() => {
    if (isRegenerating) {
      // Capture the count when regeneration started
      if (regenCountAtStart.current === null) {
        regenCountAtStart.current = review?.regeneration_count ?? 0;
      }
      // Clear loading when count increases
      if (review?.regeneration_count != null && review.regeneration_count > regenCountAtStart.current) {
        setIsRegenerating(false);
        regenCountAtStart.current = null;
      }
    } else {
      regenCountAtStart.current = null;
    }
  }, [review?.regeneration_count, isRegenerating]);

  const handleRegenerate = useCallback(() => {
    if (!contentId) return;
    setIsRegenerating(true);
    actionMutation.mutate(
      { contentId, payload: { action: 'regenerate', feedback: regenFeedback || undefined } },
      {
        onSuccess: () => {
          toast.success('Regenerating section...');
          setRegenFeedback('');
          setShowRegenFeedback(false);
        },
        onError: (err) => {
          setIsRegenerating(false);
          toast.error(`Failed to regenerate: ${(err as any)?.response?.data?.detail || err.message}`);
        },
      },
    );
  }, [contentId, regenFeedback, actionMutation]);

  // ---------------------------------------------------------------------------
  // Loading / error states
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-16 text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
        <p className="mt-2 text-muted-foreground">Loading section review...</p>
      </div>
    );
  }

  // Show post-processing state when generating beyond last section
  if (generatingNextIdx !== null && generatingNextIdx >= (review?.total_sections ?? 0)) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-16 text-center space-y-3">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-coral" />
        <p className="text-muted-foreground font-medium">Assembling article...</p>
        <p className="text-xs text-muted-foreground">
          All sections approved. Running post-processing: key takeaways, FAQ, link injection, quality check.
        </p>
      </div>
    );
  }

  // Completed redirect is handled by the useEffect above

  // 400 means content is still generating — show waiting state
  const errorStatus = (error as any)?.response?.status;
  if (isError && errorStatus === 400) {
    const statusText = progress?.status || 'generating';
    return (
      <div className="max-w-5xl mx-auto px-4 py-16 text-center space-y-3">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-coral" />
        <p className="text-muted-foreground">
          {statusText === 'outline_pending'
            ? 'Generating outline...'
            : `Generating section ${(progress?.current_section_index ?? 0) + 1}...`}
        </p>
        {progress && progress.total_sections > 0 && (
          <p className="text-xs text-muted-foreground">
            {progress.approved_count} / {progress.total_sections} sections complete
          </p>
        )}
      </div>
    );
  }

  // Backend returns 200 with status=generating_outline while outline is being created
  if (review?.status === 'generating_outline') {
    return (
      <div className="max-w-5xl mx-auto px-4 py-16 text-center space-y-3">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-coral" />
        <p className="text-muted-foreground">Generating outline...</p>
        <p className="text-xs text-muted-foreground">
          This usually takes 30-60 seconds. The page will update automatically.
        </p>
      </div>
    );
  }

  // Show generating state after outline approval
  if (outlineApproving || (review?.status && /^(in_progress|creating_strategy|auto_generating|generating|writing)/.test(review.status))) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-16 text-center space-y-3">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-coral" />
        <p className="text-muted-foreground font-medium">Generating first section...</p>
        <p className="text-xs text-muted-foreground">
          Fetching media, researching links, and writing content. This usually takes 30-60 seconds.
        </p>
      </div>
    );
  }

  // Outline is ready for approval — interactive outline editor
  if (review?.status === 'outline_pending' && contentId) {
    return (
      <OutlinePendingView
        review={review}
        contentId={contentId}
        onApprove={handleApprove}
        onCancel={goBack}
        isApprovePending={actionMutation.isPending}
      />
    );
  }

  if (isError || !review) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-16 text-center space-y-3">
        <AlertCircle className="h-8 w-8 mx-auto text-destructive" />
        <p className="text-muted-foreground">
          {(error as any)?.response?.data?.detail || 'Failed to load section review'}
        </p>
        <button
          onClick={goBack}
          className="text-sm text-coral hover:underline"
        >
          Back to content
        </button>
      </div>
    );
  }

  // Generation complete — show final review with all blocks (KT, FAQ, CTA)
  if (showFinalReview || progress?.status === 'completed' || progress?.status === 'published') {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-6 w-6 text-emerald-500" />
            <div>
              <h1 className="text-xl font-bold">Final Review</h1>
              <p className="text-sm text-muted-foreground">
                Review the complete article including Key Takeaways, FAQ, and CTA before publishing.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={goBack}
              className="px-4 py-2 text-sm border rounded-lg hover:bg-muted/50"
            >
              Back
            </button>
            <button
              onClick={() => navigate(`/editor/blog/${contentId}`)}
              className="px-6 py-2 text-sm bg-coral text-white rounded-lg hover:bg-coral-hover font-medium flex items-center gap-2"
            >
              <Check className="h-4 w-4" />
              Open in Editor
            </button>
          </div>
        </div>

        {/* Score */}
        {progress?.benchmark && (
          <div className="bg-white border rounded-lg p-4 flex items-center gap-4">
            <div className="text-2xl font-bold text-coral">{progress.benchmark.overall_score || '—'}</div>
            <div>
              <p className="text-sm font-medium">Quality Score</p>
              <p className="text-xs text-muted-foreground">{progress.approved_count} sections · {progress.edit_count} edits · {progress.regen_count} regenerations</p>
            </div>
          </div>
        )}

        {/* What was auto-generated */}
        <div className="bg-white border rounded-lg p-6 space-y-4">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Auto-Generated Blocks</h2>
          <div className="grid gap-3">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-teal-light/50 border border-teal/10">
              <span className="text-lg">✓</span>
              <div>
                <p className="font-medium text-sm">Key Takeaways</p>
                <p className="text-xs text-muted-foreground">3-5 bullet points summarizing the article's main insights. Placed after the H1 title.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg bg-teal-light/50 border border-teal/10">
              <span className="text-lg">✓</span>
              <div>
                <p className="font-medium text-sm">FAQ Section</p>
                <p className="text-xs text-muted-foreground">4-5 frequently asked questions with detailed answers. Placed after the last body section.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg bg-coral-light/50 border border-coral/10">
              <span className="text-lg">✓</span>
              <div>
                <p className="font-medium text-sm">Call to Action</p>
                <p className="text-xs text-muted-foreground">Contact button linking to your contact page. Placed at the very end.</p>
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Open in Editor to review the full article with all blocks, make final edits, and publish.</p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // Show generating animation immediately after approving
  if (generatingNextIdx !== null) {
    const totalSections = review?.total_sections || progress?.total_sections || 0;
    const approvedCount = progress?.approved_count ?? (review?.approved_sections_count ?? 0);
    return (
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">
        <div className="flex items-center gap-3">
          <button onClick={goBack} className="p-1 rounded hover:bg-muted" aria-label="Back">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-lg font-semibold">Section Review</h1>
            <p className="text-xs text-muted-foreground">Generating section {generatingNextIdx + 1} of {totalSections}</p>
          </div>
        </div>
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-3">
            <ProgressSidebar
              totalSections={totalSections}
              approvedCount={approvedCount}
              currentIndex={generatingNextIdx}
              autoApproveActive={false}
              autoApprovedCount={0}
              viewingIndex={null}
              onSectionClick={() => {}}
            />
          </div>
          <div className="col-span-9">
            <div className="bg-white rounded-lg border p-6 flex items-center justify-center min-h-[300px]">
              <div className="text-center space-y-3">
                <Loader2 className="h-10 w-10 animate-spin mx-auto text-coral" />
                <p className="text-sm font-medium">Generating section {generatingNextIdx + 1}...</p>
                <p className="text-xs text-muted-foreground">This usually takes 10-20 seconds</p>
                <p className="text-xs text-muted-foreground">{approvedCount} / {totalSections} sections approved</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={goBack}
            className="p-1 rounded hover:bg-muted"
            aria-label="Back"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-lg font-semibold">Section Review</h1>
            <p className="text-xs text-muted-foreground">
              Section {review.section_index + 1} of {review.total_sections}
              {review.regeneration_count > 0 && ` (regen #${review.regeneration_count})`}
              {' · '}{countBlockWords(review.blocks)}w this section
              {progress && ` · ${progress.approved_count} approved`}
            </p>
          </div>
        </div>

        {review.score && <ScoreBadge score={review.score} />}
      </div>

      {/* Main layout */}
      <div className="grid grid-cols-12 gap-6">
        {/* Sidebar */}
        <div className="col-span-3">
          <ProgressSidebar
            totalSections={review.total_sections}
            approvedCount={progress?.approved_count ?? review.approved_sections_count}
            currentIndex={progress?.current_section_index ?? review.section_index}
            autoApproveActive={review.auto_approve_active}
            autoApprovedCount={progress?.auto_approved_count ?? 0}
            viewingIndex={viewSection}
            onSectionClick={(idx) => { setViewSection(idx); setIsEditing(false); setIsRegenerating(false); }}
            sectionHeadings={progress?.section_headings || []}
          />
          {viewSection !== null && (
            <button
              onClick={() => { setViewSection(null); setIsRegenerating(false); }}
              className="w-full mt-2 text-xs text-coral hover:text-coral-hover underline"
            >
              Back to current section
            </button>
          )}
        </div>

        {/* Content area */}
        <div className="col-span-9">
          <div className="bg-white rounded-lg border p-6 space-y-4">
            {/* Heading (editable) + word count */}
            <div className="flex items-start justify-between gap-3">
              {isEditing ? (
                <input
                  value={editedHeading}
                  onChange={(e) => setEditedHeading(e.target.value)}
                  className="text-lg font-semibold flex-1 border-b-2 border-dashed outline-none focus:border-coral pb-1"
                  placeholder="Section heading..."
                />
              ) : (
                <h2 className="text-lg font-semibold">{review.heading}</h2>
              )}
              <div className="flex items-center gap-2 shrink-0 mt-1">
                <span className="text-xs text-muted-foreground">
                  {countBlockWords(review.blocks)}{review.target_words > 0 ? ` / ${review.target_words}` : ''} words
                </span>
                {review.blocks.some((b: TipTapBlock) => b.type === 'image' || (b.attrs as any)?.src) && (
                  <span className="text-xs bg-teal-light text-teal-dark px-1.5 py-0.5 rounded" title="Image block planned">
                    {'\uD83D\uDCF7'}
                  </span>
                )}
                {review.blocks.some((b: TipTapBlock) => b.type === 'youtube' || b.type === 'youtubeEmbed') && (
                  <span className="text-xs bg-coral-light text-coral-dark px-1.5 py-0.5 rounded" title="YouTube block planned">
                    {'\u25B6'}
                  </span>
                )}
              </div>
            </div>

            {/* TODO: Issue #10 — Add/remove sections in review (requires backend support) */}

            {/* Content display or editor */}
            {/* Regeneration loading overlay */}
            {isRegenerating && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center space-y-3">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-coral" />
                  <p className="text-sm text-muted-foreground">Regenerating section...</p>
                  <p className="text-xs text-muted-foreground">This usually takes 10-20 seconds</p>
                </div>
              </div>
            )}

            {!isRegenerating && (isEditing ? (
              <SectionEditor
                key={`editor-${review.section_index}-${review.regeneration_count}`}
                blocks={review.blocks}
                onSave={handleEdit}
                onCancel={() => setIsEditing(false)}
                isPending={actionMutation.isPending}
              />
            ) : (
              <BlocksDisplay key={`display-${review.section_index}-${review.regeneration_count}`} blocks={review.blocks} />
            ))}

            {/* Regenerate feedback input */}
            {showRegenFeedback && !isEditing && (
              <div className="flex items-start gap-2 p-3 bg-muted rounded-lg">
                <MessageSquare className="h-4 w-4 mt-2.5 text-muted-foreground shrink-0" />
                <div className="flex-1 space-y-2">
                  <textarea
                    placeholder="What should be different? (optional)"
                    value={regenFeedback}
                    onChange={(e) => setRegenFeedback(e.target.value)}
                    className="w-full px-3 py-2 text-sm border rounded-lg resize-none"
                    rows={3}
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleRegenerate}
                      disabled={actionMutation.isPending}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-coral text-white text-sm rounded-lg hover:bg-coral-hover disabled:opacity-50"
                    >
                      {actionMutation.isPending
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <RefreshCw className="h-3.5 w-3.5" />}
                      Regenerate
                    </button>
                    <button
                      onClick={() => { setShowRegenFeedback(false); setRegenFeedback(''); }}
                      className="px-3 py-1.5 border text-sm rounded-lg hover:bg-muted"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Pending section placeholder */}
            {(review as any).is_pending && (
              <div className="flex flex-col items-center justify-center py-8 text-center space-y-2">
                <div className="h-8 w-8 rounded-full border-2 border-border flex items-center justify-center">
                  <span className="text-xs text-muted-foreground">{review.section_index + 1}</span>
                </div>
                <p className="text-sm text-muted-foreground">This section hasn't been generated yet</p>
                {(review as any).key_points?.length > 0 && (
                  <div className="text-xs text-muted-foreground">
                    <p className="font-medium mb-1">Planned key points:</p>
                    <ul className="space-y-0.5">
                      {(review as any).key_points.map((kp: string, i: number) => (
                        <li key={i}>· {kp}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Approved section indicator */}
            {(review as any).is_approved && !isEditing && (
              <div className="flex items-center gap-2 pt-4 border-t text-sm text-emerald-600">
                <CheckCircle2 className="h-4 w-4" />
                This section has been approved
              </div>
            )}

            {/* Action buttons */}
            {!isEditing && !showRegenFeedback && !(review as any).is_pending && (
              <div className="flex items-center gap-2 pt-4 border-t">
                <button
                  onClick={handleApprove}
                  disabled={actionMutation.isPending}
                  className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 text-white text-sm rounded-lg hover:bg-emerald-600 disabled:opacity-50"
                  aria-label="Approve section"
                >
                  {actionMutation.isPending
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Check className="h-3.5 w-3.5" />}
                  Approve
                </button>
                <button
                  onClick={() => { setIsEditing(true); setEditedHeading(review.heading || ''); }}
                  disabled={review.blocks.length === 0}
                  className="flex items-center gap-1.5 px-4 py-2 border text-sm rounded-lg hover:bg-muted disabled:opacity-50"
                  aria-label="Edit section"
                >
                  <Edit3 className="h-3.5 w-3.5" />
                  Edit
                </button>
                <button
                  onClick={() => setShowRegenFeedback(true)}
                  disabled={actionMutation.isPending}
                  className="flex items-center gap-1.5 px-4 py-2 border text-sm rounded-lg hover:bg-muted disabled:opacity-50"
                  aria-label="Regenerate section"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Regenerate
                </button>
                <div className="flex-1" />
                <button
                  onClick={() => {
                    if (window.confirm('Skip this section? It will be removed from the final article.')) {
                      actionMutation.mutate(
                        { contentId: contentId!, payload: { action: 'skip' } },
                        {
                          onSuccess: () => toast.success('Section skipped'),
                          onError: (err) => toast.error(`Failed: ${(err as any)?.response?.data?.detail || err.message}`),
                        },
                      );
                    }
                  }}
                  disabled={actionMutation.isPending}
                  className="flex items-center gap-1.5 px-4 py-2 text-destructive border border-destructive/30 text-sm rounded-lg hover:bg-destructive/5 disabled:opacity-50"
                  aria-label="Skip section"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Skip
                </button>
              </div>
            )}

            {/* Navigation hint */}
            <div className="flex items-center justify-between pt-2 text-xs text-muted-foreground">
              <span>
                {review.is_last_section
                  ? 'Last section — approve to trigger final assembly'
                  : `Approve to generate section ${review.section_index + 2}`}
              </span>
              {progress && (
                <span>
                  {progress.edit_count > 0 && `${progress.edit_count} edits`}
                  {progress.edit_count > 0 && progress.regen_count > 0 && ' · '}
                  {progress.regen_count > 0 && `${progress.regen_count} regenerations`}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
